/**
 * Chain-switch guard (#687). While the signing code switches the wallet to chain 1337 (and for a short grace period
 * afterwards) some wallets emit events that look like a disconnect but are not:
 *
 *   - MetaMask has emitted `disconnect` with code 1013 ("Try again later") during network switches;
 *   - some wallets briefly emit `accountsChanged []` while the network changes;
 *   - every wallet emits `chainChanged` (harmless: WalletProvider only records the chain id).
 *
 * WalletProvider asks isSwitchingChain() before treating such events as a real disconnect and re-checks
 * eth_accounts once the switch has settled instead.
 */

/** How long after a switch finished we still treat disconnect-ish events as switch noise. */
export const SWITCH_GRACE_MS = 1500;

let depth = 0;
let lastEnd = -Infinity;

export function beginChainSwitch(): void {
  depth++;
}

export function endChainSwitch(): void {
  depth = Math.max(0, depth - 1);
  lastEnd = Date.now();
}

export function isSwitchingChain(now: number = Date.now()): boolean {
  return depth > 0 || now - lastEnd < SWITCH_GRACE_MS;
}

/** Run `fn` with the guard held. */
export async function withChainSwitch<T>(fn: () => Promise<T>): Promise<T> {
  beginChainSwitch();
  try {
    return await fn();
  } finally {
    endChainSwitch();
  }
}

/** Test helper. */
export function resetSwitchGuard(): void {
  depth = 0;
  lastEnd = -Infinity;
}
